import { listPatients } from "@/core/patients/patient-store";
import type { Admission } from "@/core/admissions/types";
import {
  resolveServiceFromDestination,
  type EnqueueTicketInput,
  type QueueTicket,
  type QueueTicketStatus,
  type ServiceQueueId,
} from "./types";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { shouldAutoSeedDemoData } from "@/core/demo/is-demo-mode-enabled";

const TICKETS_KEY = "hospitol.serviceQueue";
const DEMO_SEED_KEY = "hospitol.serviceQueue.demoSeeded";

function sortQueue(a: QueueTicket, b: QueueTicket): number {
  if (a.priority !== b.priority) return a.priority - b.priority;
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}

function normalizeTicket(raw: Record<string, unknown>): QueueTicket {
  return {
    id: String(raw.id ?? ""),
    patientId: String(raw.patientId ?? ""),
    admissionId: raw.admissionId ? String(raw.admissionId) : null,
    billingDocumentId: raw.billingDocumentId ? String(raw.billingDocumentId) : null,
    serviceId: (raw.serviceId as QueueTicket["serviceId"]) ?? "clinical",
    unitName: String(raw.unitName ?? ""),
    boxLabel: raw.boxLabel ? String(raw.boxLabel) : null,
    assignedPractitionerId: raw.assignedPractitionerId ? String(raw.assignedPractitionerId) : null,
    assignedPractitionerName: raw.assignedPractitionerName ? String(raw.assignedPractitionerName) : null,
    status: (raw.status as QueueTicketStatus) ?? "waiting",
    motif: String(raw.motif ?? ""),
    priority: Number(raw.priority ?? 10),
    consultationId: raw.consultationId ? String(raw.consultationId) : null,
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
    calledAt: raw.calledAt ? String(raw.calledAt) : null,
    startedAt: raw.startedAt ? String(raw.startedAt) : null,
    completedAt: raw.completedAt ? String(raw.completedAt) : null,
  };
}

function readTickets(): QueueTicket[] {
  return readJson<Record<string, unknown>[]>(TICKETS_KEY, []).map(normalizeTicket);
}

export function listQueueTickets(filter?: {
  serviceId?: ServiceQueueId;
  unitName?: string;
  status?: QueueTicketStatus | QueueTicketStatus[];
}): QueueTicket[] {
  ensureDemoQueue();
  let list = readTickets();
  if (filter?.serviceId) {
    list = list.filter((t) => t.serviceId === filter.serviceId);
  }
  if (filter?.unitName) {
    list = list.filter((t) => t.unitName === filter.unitName);
  }
  if (filter?.status) {
    const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
    list = list.filter((t) => statuses.includes(t.status));
  }
  return list.sort(sortQueue);
}

export function getQueueTicket(id: string): QueueTicket | null {
  return readTickets().find((t) => t.id === id) ?? null;
}

export function enqueueTicket(input: EnqueueTicketInput): QueueTicket {
  const now = new Date().toISOString();
  const ticket: QueueTicket = {
    id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    patientId: input.patientId,
    admissionId: input.admissionId ?? null,
    billingDocumentId: input.billingDocumentId ?? null,
    serviceId: input.serviceId,
    unitName: input.unitName,
    boxLabel: null,
    assignedPractitionerId: null,
    assignedPractitionerName: null,
    status: "waiting",
    motif: input.motif.trim(),
    priority: input.priority ?? 10,
    consultationId: null,
    createdAt: now,
    updatedAt: now,
    calledAt: null,
    startedAt: null,
    completedAt: null,
  };
  const list = readTickets();
  list.unshift(ticket);
  writeJson(TICKETS_KEY, list);
  return ticket;
}

/** Accueil — nouvelle admission enregistrée (file d'attente réception). */
export function enqueueAdmissionAtReception(input: {
  admissionId: string;
  patientId: string;
  motif: string;
}): QueueTicket {
  const existing = readTickets().find(
    (t) =>
      t.serviceId === "admission" &&
      t.admissionId === input.admissionId &&
      !t.billingDocumentId &&
      t.status !== "completed" &&
      t.status !== "cancelled",
  );
  if (existing) return existing;

  return enqueueTicket({
    patientId: input.patientId,
    admissionId: input.admissionId,
    serviceId: "admission",
    unitName: "Accueil",
    motif: input.motif,
    priority: 8,
  });
}

/** Caisse — patient ayant réglé une consultation, en attente à l'accueil. */
export function enqueuePaidConsultationAtReception(input: {
  admissionId: string;
  patientId: string;
  serviceLabel: string;
  motif: string;
  billingDocumentId: string;
}): QueueTicket {
  const existing = readTickets().find(
    (t) =>
      t.serviceId === "admission" &&
      t.billingDocumentId === input.billingDocumentId &&
      t.status !== "completed" &&
      t.status !== "cancelled",
  );
  if (existing) return existing;

  return enqueueTicket({
    patientId: input.patientId,
    admissionId: input.admissionId,
    billingDocumentId: input.billingDocumentId,
    serviceId: "admission",
    unitName: input.serviceLabel,
    motif: input.motif,
    priority: 5,
  });
}

/** Accueil — envoyer un patient payé vers la file clinique (généraliste). */
export function orientPaidTicketToClinical(ticketId: string): QueueTicket | null {
  const ticket = getQueueTicket(ticketId);
  if (!ticket || ticket.serviceId !== "admission") return null;

  enqueueTicket({
    patientId: ticket.patientId,
    admissionId: ticket.admissionId,
    billingDocumentId: ticket.billingDocumentId,
    serviceId: "clinical",
    unitName: ticket.unitName || "Médecine générale",
    motif: ticket.motif,
    priority: ticket.priority,
  });
  return completeQueueTicket(ticketId);
}

/** Orientation accueil → une ou plusieurs files d'attente. */
export function enqueueFromOrientation(
  admission: Pick<Admission, "id" | "patientId" | "motif">,
  destinations: string[],
): QueueTicket[] {
  const created: QueueTicket[] = [];
  for (const dest of destinations) {
    const trimmed = dest.trim();
    if (!trimmed) continue;
    const { serviceId, unitName } = resolveServiceFromDestination(trimmed);
    created.push(
      enqueueTicket({
        patientId: admission.patientId,
        admissionId: admission.id,
        serviceId,
        unitName,
        motif: admission.motif,
      }),
    );
  }
  return created;
}

export function callNextTicket(input: {
  serviceId: ServiceQueueId;
  unitName: string;
  boxLabel: string;
  practitionerId: string;
  practitionerName: string;
}): QueueTicket | null {
  const waiting = listQueueTickets({
    serviceId: input.serviceId,
    unitName: input.unitName,
    status: "waiting",
  });
  const next = waiting[0];
  if (!next) return null;

  const now = new Date().toISOString();
  return updateTicket(next.id, {
    status: "called",
    boxLabel: input.boxLabel,
    assignedPractitionerId: input.practitionerId,
    assignedPractitionerName: input.practitionerName,
    calledAt: now,
  });
}

export function startQueueTicket(id: string, consultationId?: string): QueueTicket | null {
  const now = new Date().toISOString();
  return updateTicket(id, {
    status: "in_progress",
    startedAt: now,
    ...(consultationId ? { consultationId } : {}),
  });
}

export function completeQueueTicket(id: string): QueueTicket | null {
  const now = new Date().toISOString();
  return updateTicket(id, { status: "completed", completedAt: now });
}

/** Termine les tickets actifs d'un patient pour un service (ex. fin consultation / dispensation). */
export function completeActiveTicketsForPatient(
  serviceId: ServiceQueueId,
  patientId: string,
): number {
  const list = readTickets();
  const now = new Date().toISOString();
  let count = 0;
  const updated = list.map((ticket) => {
    if (
      ticket.serviceId === serviceId &&
      ticket.patientId === patientId &&
      (ticket.status === "waiting" ||
        ticket.status === "called" ||
        ticket.status === "in_progress")
    ) {
      count += 1;
      return {
        ...ticket,
        status: "completed" as QueueTicketStatus,
        completedAt: now,
        updatedAt: now,
      };
    }
    return ticket;
  });
  if (count > 0) writeJson(TICKETS_KEY, updated);
  return count;
}

export function completeQueueTicketByConsultation(consultationId: string): QueueTicket | null {
  const list = readTickets();
  const ticket = list.find(
    (t) => t.consultationId === consultationId && t.status !== "completed" && t.status !== "cancelled",
  );
  if (!ticket) return null;
  return completeQueueTicket(ticket.id);
}

/** Ticket file lié à une consultation (y compris terminé). */
export function findQueueTicketByConsultation(consultationId: string): QueueTicket | null {
  const list = readTickets();
  return (
    list.find((t) => t.consultationId === consultationId && t.serviceId === "clinical") ??
    list.find((t) => t.consultationId === consultationId) ??
    null
  );
}

export function cancelQueueTicket(id: string): QueueTicket | null {
  return updateTicket(id, { status: "cancelled" });
}

function updateTicket(
  id: string,
  patch: Partial<
    Pick<
      QueueTicket,
      | "status"
      | "boxLabel"
      | "assignedPractitionerId"
      | "assignedPractitionerName"
      | "calledAt"
      | "startedAt"
      | "completedAt"
      | "consultationId"
    >
  >,
): QueueTicket | null {
  const list = readTickets();
  const index = list.findIndex((t) => t.id === id);
  if (index < 0) return null;
  const updated: QueueTicket = {
    ...list[index]!,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  list[index] = updated;
  writeJson(TICKETS_KEY, list);
  return updated;
}

export function ensureDemoQueue() {
  if (!shouldAutoSeedDemoData()) return;
  if (getStorageItem(DEMO_SEED_KEY)) return;
  const patients = listPatients();
  const p = patients[0];
  if (!p) return;

  enqueueTicket({
    patientId: p.id,
    serviceId: "clinical",
    unitName: "Médecine générale",
    motif: "Fièvre et céphalées",
    priority: 10,
  });

  if (patients[1]) {
    enqueueTicket({
      patientId: patients[1].id,
      serviceId: "clinical",
      unitName: "Médecine générale",
      motif: "Douleurs abdominales",
      priority: 15,
    });
  }

  setStorageItem(DEMO_SEED_KEY, new Date().toISOString());
}

export function countWaiting(serviceId: ServiceQueueId, unitName?: string): number {
  return listQueueTickets({
    serviceId,
    unitName,
    status: ["waiting", "called"],
  }).length;
}
